import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import {
  ExpandableSearch,
  SEARCH_MIN_CHAR_LENGTH,
} from '@rossum/components/expandable-search/ExpandableSearch';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Tile, TileSkeleton } from '@rossum/components/tile';
import { boldText } from '@rossum/components/variable-suggestions/boldText';
import { EmptyStateRadar } from '@rossum/ui';
import { IconKey } from '@rossum/ui/icons/tabler';
import { Stack } from '@rossum/ui/material';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import { credentialDetailPath, isKnownCredentialType } from '../core';
import { useGetCredentials } from '../core/hooks';
import { CredentialsHeader } from './CredentialsHeader';

export const CredentialsPage = () => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const { data: credentials, isLoading, isError } = useGetCredentials();

  const normalizedSearch = search.trim().toLowerCase();

  const filteredCredentials = useMemo(() => {
    const sorted = R.sortBy(credentials ?? [], [
      credential => credential.name.toLowerCase(),
      'asc',
    ]);

    return normalizedSearch.length >= SEARCH_MIN_CHAR_LENGTH
      ? R.pipe(
          sorted,
          R.filter(credential =>
            credential.name.toLowerCase().includes(normalizedSearch)
          )
        )
      : sorted;
  }, [normalizedSearch, credentials]);

  return (
    <>
      <HelmetComponent translationKey="features.credentials.header.title" />
      <PageLayout renderHeader={params => <CredentialsHeader {...params} />}>
        <Stack spacing={2} useFlexGap sx={{ p: 4, flex: 1 }}>
          {isLoading ? (
            Array.from({ length: 3 }, (_, index) => (
              <TileSkeleton key={index} skeletons={['icon', 'title']} />
            ))
          ) : isError ? (
            <EmptyStateRadar
              title={intl.formatMessage({
                id: 'features.credentials.error.title',
              })}
              description={intl.formatMessage({
                id: 'features.credentials.error.description',
              })}
            />
          ) : credentials && credentials.length > 0 ? (
            <Stack spacing={1} useFlexGap sx={{ flex: 1 }}>
              <ExpandableSearch
                value={search}
                onChange={setSearch}
                sx={{ alignSelf: 'flex-end', height: 'auto' }}
                placeholder={intl.formatMessage(
                  { id: 'features.credentials.search.placeholder' },
                  { minLength: SEARCH_MIN_CHAR_LENGTH }
                )}
              />
              {filteredCredentials && filteredCredentials.length > 0 ? (
                filteredCredentials.map(credential => (
                  <Tile
                    key={credential.id}
                    data-cy={`credential-tile-${credential.id}`}
                    iconProps={{
                      content: credential.hooks.length,
                    }}
                    title={credential.name}
                    to={credentialDetailPath(credential.id)}
                    description={credential.secret.baseApiUrl}
                    dragHandle={null}
                    endChips={[
                      {
                        key: 'type',
                        label: isKnownCredentialType(credential.type)
                          ? intl.formatMessage({
                              id: `features.credentials.type.${credential.type}`,
                            })
                          : credential.type,
                      },
                    ]}
                  />
                ))
              ) : (
                <EmptyStateRadar
                  title={intl.formatMessage({
                    id: 'features.credentials.noResults.title',
                  })}
                  description={intl.formatMessage(
                    {
                      id: 'features.credentials.noResults.description',
                    },
                    { search: search.trim(), boldText }
                  )}
                />
              )}
            </Stack>
          ) : (
            <Stack sx={{ flex: 1 }}>
              <EmptyStateRadar
                iconProps={{ Icon: IconKey, iconSize: 36 }}
                title={intl.formatMessage({
                  id: 'features.credentials.empty.title',
                })}
                description={intl.formatMessage({
                  id: 'features.credentials.empty.description',
                })}
              />
            </Stack>
          )}
        </Stack>
      </PageLayout>
    </>
  );
};
